import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { getVoiceFallbackClient } from './_shared';

// ── Callbacks (/api/callbacks) ────────────────────────────────────────
// Field names verified 2026-08-19 against the live CallbackResponse/
// CallbackUpdate schema on /openapi.json — the previous version of this
// section guessed a shape (phone/scheduled_at, no priority) that didn't
// match the real API at all.

export type CallbackStatus = 'pending' | 'scheduled' | 'completed' | 'failed' | 'cancelled';
export type CallbackPriority = 'low' | 'normal' | 'high' | 'urgent';

export type CallbackRecord = {
  id: string;
  session_id: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  contact_phone: string;
  preferred_time: string | null;
  priority: string;
  status: string;
  assigned_to: string | null;
  notes: string | null;
  completed_at: string | null;
  created_at: string;
};

export type CallbacksFilter = { workspace_id_q?: string; status?: string; priority?: string; limit?: number };

/** `GET /api/callbacks` — list callbacks with optional filtering. */
export async function fetchCallbacks(filter: CallbacksFilter = {}, axiosInstance?: AxiosInstance): Promise<CallbackRecord[]> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<CallbackRecord[]>(`${apiConfig.voiceEndpoints.callbacks}`, { params: filter });
  return Array.isArray(response.data) ? response.data : [];
}

/** `GET /api/callbacks/summary` — the backend declares no response schema for
 * this (untyped dict), so its exact field names can't be verified. The
 * callbacks page computes its status counts from the real, typed list
 * (`fetchCallbacks`) instead of relying on this — kept for completeness/future
 * use, but treat any field on the result as unconfirmed. */
export async function fetchCallbacksSummary(axiosInstance?: AxiosInstance): Promise<Record<string, unknown>> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<Record<string, unknown>>(`${apiConfig.voiceEndpoints.callbacks}/summary`);
  return response.data ?? {};
}

/** `PATCH /api/callbacks/{callback_id}` — update a callback record. */
export async function updateCallback(
  callbackId: string,
  patch: { status?: CallbackStatus; priority?: CallbackPriority; assigned_to?: string; notes?: string },
  axiosInstance?: AxiosInstance
): Promise<CallbackRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.patch<CallbackRecord>(`${apiConfig.voiceEndpoints.callbacks}/${callbackId}`, patch);
  return response.data;
}

/** `DELETE /api/callbacks/{callback_id}` — delete a callback. */
export async function deleteCallback(callbackId: string, axiosInstance?: AxiosInstance): Promise<boolean> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.delete(`${apiConfig.voiceEndpoints.callbacks}/${callbackId}`);
  return response.status === 200 || response.status === 204;
}

/** `POST /api/callbacks/{callback_id}/dispatch-now` — dispatch a pending callback immediately. */
export async function dispatchCallbackNow(callbackId: string, axiosInstance?: AxiosInstance): Promise<CallbackRecord> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.post<CallbackRecord>(`${apiConfig.voiceEndpoints.callbacks}/${callbackId}/dispatch-now`);
  return response.data;
}
